import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Key, Shield, Plus, Trash2, Zap, ExternalLink, Check, X,
  AlertCircle, Copy, Eye, EyeOff, RefreshCw, ChevronDown, ChevronRight,
  BookOpen, Link2, Globe, Lock, Unlock, Users, Database,
  UserCheck, UserPlus, BarChart2, ArrowRight, LogIn
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BubbleLogo = () => (
  <svg viewBox="0 0 40 40" className="w-8 h-8 flex-shrink-0">
    <circle cx="20" cy="20" r="18" fill="#0D1117" />
    <circle cx="20" cy="20" r="12" fill="#1a73e8" />
    <circle cx="20" cy="20" r="6" fill="white" />
  </svg>
);

const GoogleLogo = () => (
  <svg viewBox="0 0 40 40" className="w-8 h-8 flex-shrink-0">
    <circle cx="20" cy="20" r="18" fill="#fff" />
    <text x="8" y="27" fontSize="20" fontFamily="Arial">G</text>
  </svg>
);

const MicrosoftLogo = () => (
  <svg viewBox="0 0 40 40" className="w-8 h-8 flex-shrink-0">
    <rect x="4" y="4" width="15" height="15" fill="#f25022" />
    <rect x="21" y="4" width="15" height="15" fill="#7fba00" />
    <rect x="4" y="21" width="15" height="15" fill="#00a4ef" />
    <rect x="21" y="21" width="15" height="15" fill="#ffb900" />
  </svg>
);

const getProviderIcon = (type) => {
  if (type === 'bubble') return <BubbleLogo />;
  if (type === 'google') return <GoogleLogo />;
  if (type === 'microsoft') return <MicrosoftLogo />;
  return <Key className="w-8 h-8 text-gray-400" />;
};

// ── Panel: Sincronización masiva Bubble ───────────────────────────────────────
const BubbleSyncPanel = ({ provider }) => {
  const [apiKey, setApiKey] = useState('');
  const [appName, setAppName] = useState(provider?.app_name || '');
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSync = async () => {
    if (!apiKey.trim()) { toast.error('Introduce el API Token de administrador de Bubble'); return; }
    if (!appName.trim()) { toast.error('Introduce el nombre de la app en Bubble'); return; }
    setSyncing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/oauth/bubble/sync-users`, {
        app_name: appName,
        api_key: apiKey,
        provider_id: provider?.id,
      });
      setResult(res.data);
      toast.success(`Sincronización lista: ${res.data.created} creados, ${res.data.updated} actualizados`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error de sincronización';
      toast.error(typeof msg === 'string' ? msg : 'Error de sincronización');
      setResult({ success: false, message: typeof msg === 'string' ? msg : 'Error' });
    }
    setSyncing(false);
  };

  return (
    <div className="mt-4 p-5 bg-blue-500/5 border border-blue-500/20 rounded-xl space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <Database size={16} className="text-blue-400" />
        <h5 className="text-white font-medium text-sm">Sincronización masiva de usuarios Bubble</h5>
      </div>
      <p className="text-gray-500 text-xs">
        Importa todos los usuarios de tu app de Bubble al instante. Requiere el <strong className="text-gray-300">API Token</strong> de administrador
        (Settings → API → Bubble API Token).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-gray-500 text-xs mb-1.5 block">Nombre de la app en Bubble</label>
          <input
            value={appName}
            onChange={e => setAppName(e.target.value)}
            placeholder="mi-aplicacion"
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-400"
          />
        </div>
        <div>
          <label className="text-gray-500 text-xs mb-1.5 block">API Token de Administrador *</label>
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Bearer token de Bubble"
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-400"
          />
        </div>
      </div>

      <button
        onClick={handleSync}
        disabled={syncing}
        className="w-full py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        {syncing ? <RefreshCw size={14} className="animate-spin" /> : <Database size={14} />}
        {syncing ? 'Sincronizando usuarios...' : 'Importar todos los usuarios de Bubble'}
      </button>

      {result && (
        <div className={`p-4 rounded-xl text-sm ${result.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
          <div className="flex items-center gap-2 font-medium mb-2">
            {result.success ? <Check size={14} className="text-green-400" /> : <X size={14} className="text-red-400" />}
            <span className={result.success ? 'text-green-400' : 'text-red-400'}>{result.message}</span>
          </div>
          {result.success && (
            <div className="grid grid-cols-3 gap-3 mt-2">
              {[
                { label: 'Creados', val: result.created, color: 'text-green-400' },
                { label: 'Actualizados', val: result.updated, color: 'text-blue-400' },
                { label: 'Con error', val: result.failed, color: 'text-red-400' },
              ].map(s => (
                <div key={s.label} className="bg-white/5 rounded-lg p-2 text-center">
                  <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
                  <p className="text-gray-500 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-2 text-xs text-red-400/70 space-y-0.5">
              {result.errors.map((e, i) => <p key={i}>• {e}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Panel: Usuarios registrados vía SSO ───────────────────────────────────────
const SSOUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, synced: 0 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/oauth/sso/users?limit=50`);
      setUsers(res.data.users || []);
      setStats({ total: res.data.total || 0, synced: res.data.synced || 0 });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const providerBadge = (p) => {
    const colors = { bubble: 'bg-blue-500/20 text-blue-400', google: 'bg-red-500/20 text-red-400', microsoft: 'bg-blue-600/20 text-blue-300' };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[p] || 'bg-gray-500/20 text-gray-400'}`}>{p}</span>;
  };

  return (
    <div className="glass-panel p-5">
      <button onClick={() => { setOpen(o => !o); if (!open) load(); }} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Users size={16} className="text-purple-400" />
          </div>
          <div className="text-left">
            <p className="text-white font-medium text-sm">Usuarios registrados vía SSO</p>
            <p className="text-gray-500 text-xs">{stats.total} total · {stats.synced} sincronizados desde Bubble</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-purple-400 font-bold text-lg">{stats.total}</span>
          {open ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
        </div>
      </button>

      {open && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="grid grid-cols-3 gap-3 flex-1">
              {[
                { icon: UserPlus, label: 'Via SSO OAuth', val: stats.total - stats.synced, color: 'text-green-400' },
                { icon: Database, label: 'Via Sync Bubble', val: stats.synced, color: 'text-blue-400' },
                { icon: UserCheck, label: 'Total SSO', val: stats.total, color: 'text-purple-400' },
              ].map(s => (
                <div key={s.label} className="bg-white/5 rounded-xl p-3 text-center">
                  <s.icon size={14} className={`${s.color} mx-auto mb-1`} />
                  <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
                  <p className="text-gray-500 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
            <button onClick={load} className="ml-3 p-2 hover:bg-white/10 rounded-lg"><RefreshCw size={14} className="text-gray-400" /></button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={20} className="text-purple-400 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Users size={28} className="mx-auto mb-2 opacity-40" />
              Aún no hay usuarios SSO registrados
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-3 p-3 bg-white/3 hover:bg-white/5 rounded-xl transition-colors">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    {u.avatar_url
                      ? <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      : <span className="text-white text-xs font-bold">{(u.name || u.okuser || '?')[0].toUpperCase()}</span>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{u.name || u.okuser}</p>
                    <p className="text-gray-500 text-xs truncate">{u.email || `@${u.okuser}`}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {providerBadge(u.oauth_provider)}
                    {u.sso_synced && <span className="text-xs text-green-400/70">synced</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const OAuthSSOSection = () => {
  const [templates, setTemplates] = useState({});
  const [providers, setProviders] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [expandedInstructions, setExpandedInstructions] = useState(null);
  const [expandedSync, setExpandedSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState({});

  const [newProvider, setNewProvider] = useState({
    provider_type: '', client_id: '', client_secret: '',
    redirect_uri: '', app_name: '', enabled: true,
  });

  useEffect(() => {
    loadTemplates();
    loadProviders();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/oauth/providers/templates`);
      setTemplates(res.data.templates || {});
    } catch {}
  };

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API}/oauth/providers`);
      setProviders(res.data || []);
    } catch {}
  };

  const selectTemplate = (key) => {
    setSelectedTemplate(key);
    const template = templates[key];
    setNewProvider({
      provider_type: key,
      client_id: '', client_secret: '',
      redirect_uri: `${process.env.REACT_APP_BACKEND_URL || 'https://okbot.okgo.mx'}/api/oauth/callback/${key}`,
      app_name: '', enabled: true,
    });
    setShowAddProvider(true);
  };

  const safeErrorMsg = (e, fallback = 'Error inesperado') => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const f = detail[0];
      return `Error en "${f?.loc?.slice(-1)[0] || 'campo'}": ${f?.msg || 'inválido'}`;
    }
    return e?.message || fallback;
  };

  const createProvider = async () => {
    if (!newProvider.client_id || !newProvider.client_secret) {
      toast.error('Client ID y Client Secret son requeridos'); return;
    }
    if (newProvider.provider_type === 'bubble' && !newProvider.app_name) {
      toast.error('El nombre de la app en Bubble es requerido'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/oauth/providers`, {
        provider_type: newProvider.provider_type,
        name: templates[newProvider.provider_type]?.name || newProvider.provider_type,
        client_id: newProvider.client_id,
        client_secret: newProvider.client_secret,
        redirect_uri: newProvider.redirect_uri,
        app_name: newProvider.app_name || '',
        enabled: newProvider.enabled,
      });
      toast.success('Proveedor OAuth configurado');
      setShowAddProvider(false);
      setSelectedTemplate(null);
      setNewProvider({ provider_type: '', client_id: '', client_secret: '', redirect_uri: '', app_name: '', enabled: true });
      loadProviders();
    } catch (e) {
      toast.error(safeErrorMsg(e, 'Error al crear proveedor'));
    }
    setLoading(false);
  };

  const toggleProvider = async (id, current) => {
    try {
      await axios.put(`${API}/oauth/providers/${id}`, { enabled: !current });
      toast.success(`Proveedor ${!current ? 'habilitado' : 'deshabilitado'}`);
      loadProviders();
    } catch (e) { toast.error(safeErrorMsg(e)); }
  };

  const deleteProvider = async (id) => {
    if (!window.confirm('¿Eliminar esta configuración OAuth?')) return;
    try {
      await axios.delete(`${API}/oauth/providers/${id}`);
      toast.success('Proveedor eliminado');
      loadProviders();
    } catch (e) { toast.error(safeErrorMsg(e)); }
  };

  const testConnection = async (id) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/oauth/providers/${id}/test`);
      setTestResult({ providerId: id, ...res.data });
      if (res.data.success) toast.success('Configuración válida');
      else toast.warning('Configuración incompleta');
    } catch (e) { toast.error(safeErrorMsg(e)); }
    setLoading(false);
  };

  const initiateSSO = async (providerType) => {
    try {
      const res = await axios.get(`${API}/oauth/sso/initiate/${providerType}`);
      window.open(res.data.auth_url, '_blank', 'width=500,height=650');
    } catch (e) {
      toast.error(safeErrorMsg(e, 'Error al iniciar SSO'));
    }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado'); };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-3">
            <Key className="text-purple-400" />OAuth SSO
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Flujo completo: login con proveedor → creación automática de usuario → sesión en OkBot
          </p>
        </div>
        <button onClick={() => setShowAddProvider(!showAddProvider)} className="btn-primary flex items-center gap-2">
          <Plus size={16} />Agregar Proveedor
        </button>
      </div>

      {/* Resumen del flujo */}
      <div className="glass-panel p-4 bg-purple-500/5 border border-purple-500/20">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-purple-400" />
          <span className="text-white text-sm font-medium">Flujo SSO implementado</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            'Usuario hace clic en "Entrar con [Proveedor]"',
            'OkBot redirige al proveedor',
            'Usuario se autentica',
            'OkBot recibe el código OAuth',
            'Intercambia por token + perfil',
            'Crea/actualiza usuario en OkBot',
            'Inicia sesión automáticamente',
          ].map((step, i, arr) => (
            <React.Fragment key={i}>
              <span className="text-xs text-gray-300 bg-white/5 px-2 py-1 rounded-lg">{step}</span>
              {i < arr.length - 1 && <ArrowRight size={12} className="text-purple-400 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Panel usuarios SSO */}
      <SSOUsersPanel />

      {/* Agregar proveedor */}
      {showAddProvider && (
        <div className="glass-panel p-6">
          <h3 className="text-white font-medium mb-4">Selecciona un Proveedor OAuth</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {Object.entries(templates).map(([key, tmpl]) => {
              const isConfigured = providers.some(p => p.provider_type === key);
              return (
                <button
                  key={key}
                  onClick={() => !isConfigured && selectTemplate(key)}
                  disabled={isConfigured}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    selectedTemplate === key
                      ? 'border-purple-500 bg-purple-500/10'
                      : isConfigured
                        ? 'border-gray-700 bg-gray-800/50 opacity-50 cursor-not-allowed'
                        : 'border-white/10 hover:border-purple-500/50 hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {getProviderIcon(key)}
                    <div>
                      <p className="text-white font-medium">{tmpl.name}</p>
                      {isConfigured && <span className="text-xs text-green-400">Configurado</span>}
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs">{
                    key === 'bubble' ? 'Integra SSO con tu app de Bubble.io + sync masivo' :
                    key === 'google' ? 'Login con cuentas de Google' :
                    'Login con Microsoft/Azure AD'
                  }</p>
                </button>
              );
            })}
          </div>

          {selectedTemplate && (
            <div className="border-t border-white/10 pt-6 mt-2 space-y-5">
              <div className="flex items-center gap-2">
                {getProviderIcon(selectedTemplate)}
                <h4 className="text-white font-medium">Configurar {templates[selectedTemplate]?.name}</h4>
              </div>

              {/* Instrucciones */}
              {templates[selectedTemplate]?.instructions && (
                <div className="bg-black/20 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedInstructions(expandedInstructions === selectedTemplate ? null : selectedTemplate)}
                    className="w-full p-4 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <BookOpen className="text-purple-400" size={16} />
                      <span className="text-white font-medium text-sm">{templates[selectedTemplate].instructions.title}</span>
                    </div>
                    {expandedInstructions === selectedTemplate
                      ? <ChevronDown size={16} className="text-gray-400" />
                      : <ChevronRight size={16} className="text-gray-400" />}
                  </button>
                  {expandedInstructions === selectedTemplate && (
                    <div className="px-4 pb-4 space-y-3">
                      <p className="text-gray-400 text-sm">{templates[selectedTemplate].instructions.description}</p>
                      {templates[selectedTemplate].instructions.steps?.map((s, i) => (
                        <div key={i} className="flex gap-3">
                          <div className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{s.step}</div>
                          <div>
                            <p className="text-white text-sm font-medium">{s.title}</p>
                            <p className="text-gray-500 text-xs mt-0.5">{s.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Campos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedTemplate === 'bubble' && (
                  <div className="md:col-span-2">
                    <label className="text-gray-400 text-xs mb-1.5 block uppercase tracking-wider">Nombre de tu App en Bubble *</label>
                    <input
                      value={newProvider.app_name}
                      onChange={e => setNewProvider({ ...newProvider, app_name: e.target.value })}
                      placeholder="mi-aplicacion (sin .bubbleapps.io)"
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-purple-400"
                    />
                    <p className="text-gray-600 text-xs mt-1">
                      URL: <span className="text-purple-400">{newProvider.app_name || 'tu-app'}</span>.bubbleapps.io
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block uppercase tracking-wider">Client ID *</label>
                  <input
                    value={newProvider.client_id}
                    onChange={e => setNewProvider({ ...newProvider, client_id: e.target.value })}
                    placeholder="Tu Client ID"
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1.5 block uppercase tracking-wider">Client Secret *</label>
                  <div className="relative">
                    <input
                      type={showSecret.new ? 'text' : 'password'}
                      value={newProvider.client_secret}
                      onChange={e => setNewProvider({ ...newProvider, client_secret: e.target.value })}
                      placeholder="Tu Client Secret"
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 pr-10 text-white placeholder-gray-600 focus:outline-none focus:border-purple-400"
                    />
                    <button type="button" onClick={() => setShowSecret(s => ({ ...s, new: !s.new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                      {showSecret.new ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-gray-400 text-xs mb-1.5 block uppercase tracking-wider">Redirect URI (copia esto en el proveedor)</label>
                  <div className="flex gap-2">
                    <input value={newProvider.redirect_uri}
                      onChange={e => setNewProvider({ ...newProvider, redirect_uri: e.target.value })}
                      className="flex-1 bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-xs"
                    />
                    <button onClick={() => copyToClipboard(newProvider.redirect_uri)} className="btn-secondary px-3" title="Copiar">
                      <Copy size={15} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setShowAddProvider(false); setSelectedTemplate(null); }} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button onClick={createProvider} disabled={loading || !newProvider.client_id || !newProvider.client_secret}
                  className="btn-primary flex-1 flex items-center justify-center gap-2">
                  {loading ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  Guardar Configuración
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lista de proveedores configurados */}
      <div className="space-y-4">
        <h3 className="text-white font-medium">Proveedores Configurados</h3>
        {providers.length === 0 ? (
          <div className="glass-panel p-10 text-center">
            <Key className="mx-auto text-gray-600 mb-3" size={40} />
            <p className="text-gray-500">No hay proveedores OAuth configurados</p>
            <p className="text-gray-600 text-sm mt-1">Agrega un proveedor para habilitar Single Sign-On completo</p>
          </div>
        ) : (
          providers.map(provider => (
            <div key={provider.id} className="glass-panel p-5 space-y-4">
              {/* Header del proveedor */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {getProviderIcon(provider.provider_type)}
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-medium">{provider.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${provider.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {provider.enabled ? '● Activo' : '○ Inactivo'}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Client ID: <span className="font-mono text-gray-400">{provider.client_id?.substring(0, 24)}…</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {provider.enabled && (
                    <button
                      onClick={() => initiateSSO(provider.provider_type)}
                      className="px-3 py-1.5 bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 border border-purple-500/30 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                      title="Iniciar flujo SSO de prueba"
                    >
                      <LogIn size={13} />SSO Login
                    </button>
                  )}
                  {provider.provider_type === 'bubble' && (
                    <button
                      onClick={() => setExpandedSync(expandedSync === provider.id ? null : provider.id)}
                      className="px-3 py-1.5 bg-blue-500/15 hover:bg-blue-500/25 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                      title="Sincronizar usuarios de Bubble"
                    >
                      <RefreshCw size={13} className={expandedSync === provider.id ? 'animate-spin' : ''} />Sync
                    </button>
                  )}
                  <button onClick={() => testConnection(provider.id)} disabled={loading}
                    className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5">
                    <Zap size={13} />Probar
                  </button>
                  <button onClick={() => toggleProvider(provider.id, provider.enabled)}
                    className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors ${
                      provider.enabled ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}>
                    {provider.enabled ? <Lock size={13} /> : <Unlock size={13} />}
                    {provider.enabled ? 'Deshabilitar' : 'Habilitar'}
                  </button>
                  <button onClick={() => deleteProvider(provider.id)}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {/* Resultado del test */}
              {testResult?.providerId === provider.id && (
                <div className={`p-4 rounded-xl ${testResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {testResult.success ? <Check size={14} className="text-green-400" /> : <X size={14} className="text-red-400" />}
                    <span className={`text-sm font-medium ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>{testResult.message}</span>
                  </div>
                  {testResult.errors?.map((e, i) => <p key={i} className="text-red-400/70 text-xs ml-5">• {e}</p>)}
                  {testResult.test_auth_url && (
                    <a href={testResult.test_auth_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-purple-400 hover:text-purple-300 text-sm">
                      <ExternalLink size={13} />Abrir enlace de prueba OAuth
                    </a>
                  )}
                </div>
              )}

              {/* Detalles */}
              <div className="pt-3 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><p className="text-gray-500 mb-0.5">Tipo</p><p className="text-white capitalize">{provider.provider_type}</p></div>
                <div><p className="text-gray-500 mb-0.5">Scopes</p><p className="text-gray-400">{provider.scopes?.join(', ') || 'N/A'}</p></div>
                <div className="md:col-span-2">
                  <p className="text-gray-500 mb-0.5">Redirect URI</p>
                  <div className="flex items-center gap-2">
                    <p className="text-gray-400 font-mono truncate">{provider.redirect_uri}</p>
                    <button onClick={() => copyToClipboard(provider.redirect_uri)} className="flex-shrink-0 text-gray-600 hover:text-white">
                      <Copy size={11} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Panel de sync masivo para Bubble — se abre con el botón Sync del header */}
              {provider.provider_type === 'bubble' && expandedSync === provider.id && (
                <BubbleSyncPanel provider={provider} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OAuthSSOSection;
